<script setup lang="ts">
import InputAddress from "~/components/component/Service/order/forms/address/InputAddress.vue";
import type {AddressErrors} from "~/types/addressTypes";
import {postStoreAddress} from "~/composables/api/profile/address";
import {useStoreSelectedAddress} from "~/composables/store/user/address/store";
import {scrollToAnchor} from "~/utils/scrollToAnchor";
import {useIsHouseHomeType} from "~/composables/store/services/service_inputs";


const props = defineProps({
  showBackButton:{
    type: Boolean,
    default: true
  },
  showSaveButton: {
    type: Boolean,
    default: true
  }
})
const emit = defineEmits(['back', 'next','setAddress'])
const {$toast, $toastError} = useNuxtApp()
const route = useRoute()
const isBccPage = computed(() => route.name === 'bcc')
const addresses = useStoreAddresses()

const userAddress = ref({
  city: useStoreUserCity().value,
  street: '',
  house: '',
  block: '',
  entrance: '',
  flat: '',
  floor: '',
  doorphone: '',
  complex: '',
  latitude: '',
  longitude: '',
  has_cat: false,
  has_dog: false,
  has_pet: false,
  notes: ''
})

const addressErrors = ref(<AddressErrors>{
  city: {
    isError: false,
    text: ''
  },
  street: {
    isError: false,
    text: ''
  },
  house: {
    isError: false,
    text: ''
  },
  isError: false
})

const isHouseType = useIsHouseHomeType()

const ctaDisabled = computed(() => {
  if (!userAddress.value.street?.trim()) {
    return true
  }
  if (isBccPage.value) {
    if (!userAddress.value.house?.trim()) {
      return true
    }
    // у частного дома нет квартиры, подъезда и этажа — поля скрыты, требовать их не нужно
    if (isHouseType.value) {
      return false
    }
    return !userAddress.value.flat?.trim() || !userAddress.value.entrance?.trim() || !userAddress.value.floor?.trim()
  }
  return false
})

defineExpose({handleAddAddress, ctaDisabled})

async function handleAddAddress() {
  addressErrors.value = useValidateAddress(userAddress.value, isBccPage.value)
  if (addressErrors.value.isError) {
    return false
  }
  const data = {
    location_id: userAddress.value.city.location_id,
    ...userAddress.value
  }
  try {
    const {data: response, error, pending} = await postStoreAddress(data)
    if (response.value) {
      const responseData = response.value as {address_id: number, message: string}
      const newAddress = {
        address_id: responseData.address_id,
        ...JSON.parse(JSON.stringify(userAddress.value))
      }
      addresses.value.push(newAddress)
      useStoreSelectedAddress().value = newAddress
      $toast(responseData.message)
      scrollToAnchor('address-manager')
      handleNext()
      return {response: response.value, address: {
        address_id: response.value?.address_id, ...userAddress.value
        }}
    }
  } catch (e) {
    console.log(e)
  }
}

function handleBack() {
  emit('back')
}
function handleNext() {
  emit('next')
}
</script>

<template>
  <div>
    <form @submit.prevent="handleAddAddress" class="order-form">
      <InputAddress v-model="userAddress" :errors="addressErrors" :key="addressErrors"/>
    </form>
    <template v-if="isBccPage">
      <div v-if="showBackButton || showSaveButton" class="flex gap-3 items-center mt-5">
        <button
            v-if="showBackButton"
            type="button"
            @click="handleBack"
            class="flex-1 h-[50px] rounded-2xl border-[1.5px] border-[#E8EAE8] bg-white text-[#2c3530] text-[14px] font-bold"
        >{{ $t('input_forms.button.back') }}</button>
        <button
            v-if="showSaveButton"
            type="button"
            :disabled="ctaDisabled"
            @click="handleAddAddress"
            class="flex-1 h-[50px] rounded-2xl bg-primary-green text-white text-[14px] font-bold disabled:opacity-50 disabled:cursor-default"
        >{{ $t('input_forms.button.save') }}</button>
      </div>
    </template>
    <div v-else class="flex md:flex-row flex-col gap-3 items-center mt-5">
      <UIBtn
          v-if="showBackButton"
          :color="'light-gray'" class="rounded-full md:w-fit w-full"
          @click="handleBack">{{
          $t('input_forms.button.back')
        }}
      </UIBtn>
      <UIBtn
          v-if="showSaveButton"
          @click="handleAddAddress"
          :color="'green'"
          :disabled="ctaDisabled"
          class="rounded-full md:w-fit w-full"
      >{{
          $t('input_forms.button.save')
        }}
      </UIBtn>
    </div>
  </div>
</template>

<style scoped>

</style>